import type { PropositionDocument } from "@dune/types";
import { Button } from "@dune/ui";
import { formatDateTime } from "@dune/utils";
import type { ColumnDef } from "@tanstack/react-table";
import { Download, FileText, Loader2, Trash2 } from "lucide-react";
import React, { useCallback } from "react";
import { useDocumentUrl } from "../../../../hooks/useDocumentUrl";
import { downloadFileWithName } from "../../../../utils/document.utils";

interface DocumentsColumnsProps {
	loadingDocumentId: string | null;
	downloadingDocumentId: string | null;
	setDownloadingDocumentId: (id: string | null) => void;
	onDelete?: (documentId: string) => void;
	deletingDocumentId?: string | null;
	isDuneUser: boolean;
}

const statusConfig: Record<
	string,
	{ label: string; color: "orange" | "green" | "red" | "gray" }
> = {
	TO_BE_PROCESSED: { label: "À traiter", color: "orange" },
	COMPLIANT: { label: "Conforme", color: "green" },
	NON_COMPLIANT: { label: "Non conforme", color: "red" },
	NOT_APPLICABLE: { label: "N/A", color: "gray" },
};

const colorClasses: Record<"orange" | "green" | "red" | "gray", string> = {
	orange:
		"text-orange-badge-text bg-orange-badge-background border-orange-badge-border",
	green:
		"text-green-badge-text bg-green-badge-background border-green-badge-border",
	red: "text-red-badge-text bg-red-badge-background border-red-badge-border",
	gray: "text-gray-badge-text bg-gray-badge-background border-gray-badge-border",
};

export const propositionDocumentsColumns = ({
	loadingDocumentId,
	downloadingDocumentId,
	setDownloadingDocumentId,
	onDelete,
	deletingDocumentId,
	isDuneUser,
}: DocumentsColumnsProps): ColumnDef<PropositionDocument>[] => {
	return [
		{
			accessorKey: "name",
			header: "Nom",
			cell: ({ row }) => {
				const doc = row.original;
				const docId = doc.id.toString();
				const isLoadingDoc = loadingDocumentId === docId;

				return (
					<div className="flex items-center gap-3 min-w-0">
						{isLoadingDoc ? (
							<Loader2 size={16} className="animate-spin flex-shrink-0" />
						) : (
							<FileText size={16} className="flex-shrink-0" />
						)}
						<span className="truncate max-w-xs" title={doc.file.name}>
							{doc.file.name}
						</span>
					</div>
				);
			},
			size: 300,
		},
		{
			accessorKey: "type",
			header: "Type",
			cell: ({ row }) => {
				const doc = row.original;
				return (
					<div className="inline-flex items-center px-2 py-1 rounded-full text-xs text-gray-badge-text bg-gray-badge-background border border-gray-badge-border whitespace-nowrap">
						{doc.type}
					</div>
				);
			},
			size: 150,
		},
		{
			accessorKey: "createdAt",
			header: "Créé le",
			cell: ({ row }) => {
				const doc = row.original;
				return (
					<div className="text-sm whitespace-nowrap">
						{formatDateTime(doc.createdAt)}
					</div>
				);
			},
			size: 120,
		},
		{
			accessorKey: "updatedAt",
			header: "Modifié le",
			cell: ({ row }) => {
				const doc = row.original;
				return (
					<div className="text-sm whitespace-nowrap">
						{formatDateTime(doc.updatedAt)}
					</div>
				);
			},
			size: 120,
		},
		{
			accessorKey: "status",
			header: "Statut",
			cell: ({ row }) => {
				const doc = row.original;
				const status = doc.file.humanProcessingStatus;
				const cfg = statusConfig[status as keyof typeof statusConfig] || {
					label: status,
					color: "orange",
				};
				const badgeClasses =
					colorClasses[(cfg.color as keyof typeof colorClasses) ?? "orange"] ||
					colorClasses.orange;

				return (
					<div
						className={`inline-flex items-center px-2 py-1 rounded-full text-xs border whitespace-nowrap ${badgeClasses}`}
					>
						{cfg.label}
					</div>
				);
			},
			size: 160,
		},
		{
			accessorKey: "size",
			header: "Taille",
			cell: () => {
				return <div className="text-sm text-gray-600">—</div>;
			},
			size: 100,
		},
		{
			id: "actions",
			header: "Actions",
			cell: ({ row }) => {
				const doc = row.original;
				const docId = doc.id.toString();
				const isDownloadingDoc = downloadingDocumentId === docId;
				const isDeletingDoc = deletingDocumentId === docId;

				const { data: documentUrl, refetch: fetchDocumentUrl } = useDocumentUrl(
					{
						bucket: "private",
						key: doc.file.key,
						documentId: docId,
					},
					false,
				);

				const handleDownload = useCallback(
					async (e: React.MouseEvent) => {
						e.stopPropagation();

						if (isDownloadingDoc) return;

						setDownloadingDocumentId(docId);

						try {
							let url = documentUrl;
							if (!url) {
								const result = await fetchDocumentUrl();
								url = result.data;
							}

							if (url) {
								await downloadFileWithName(url, doc.file.name);
							}
						} catch (error) {
							console.error(
								"Erreur lors du téléchargement du document:",
								error,
							);
						} finally {
							setDownloadingDocumentId(null);
						}
					},
					[
						docId,
						documentUrl,
						fetchDocumentUrl,
						isDownloadingDoc,
						setDownloadingDocumentId,
						doc.file.name,
					],
				);

				const handleDelete = useCallback(
					(e: React.MouseEvent) => {
						e.stopPropagation();
						if (isDeletingDoc || !onDelete) return;

						onDelete(docId);
					},
					[docId, isDeletingDoc, onDelete],
				);

				return (
					<div className="flex justify-center gap-1">
						<Button
							variant="ghost"
							size="sm"
							className="h-8 w-8 p-0"
							aria-label="download"
							disabled={isDownloadingDoc}
							onClick={handleDownload}
						>
							{isDownloadingDoc ? (
								<Loader2 size={14} className="animate-spin" />
							) : (
								<Download size={14} />
							)}
						</Button>
						{onDelete && isDuneUser && (
							<Button
								variant="ghost"
								size="sm"
								className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
								aria-label="delete"
								disabled={isDeletingDoc}
								onClick={handleDelete}
							>
								{isDeletingDoc ? (
									<Loader2 size={14} className="animate-spin" />
								) : (
									<Trash2 size={14} />
								)}
							</Button>
						)}
					</div>
				);
			},
			size: 100,
		},
	];
};
